import React, { useEffect } from "react";
import { Typography, Grid, Box, TextField, FormControlLabel, Checkbox } from "@mui/material";
import { useFormikContext } from "formik";
import { checkboxes, providerTypes } from "./providerTypes";

interface ProvidersFormDialogROIFormProps {
  submitAttempted?: boolean;
}

const ProvidersFormDialogROIForm: React.FC<ProvidersFormDialogROIFormProps> = ({ submitAttempted }) => {
  const { values, errors, touched, handleChange, handleBlur, setFieldValue, setFieldTouched } = useFormikContext<any>();
  const other = values.other;
  const providerType = values.provider?.type;

  // Set default checkbox values based on provider type
  useEffect(() => {
    if (providerType) {
      const defaults =
        providerTypes.find((p) => p.value === providerType)?.defaultDisclosureInfo || [];
      const nonDefaults = checkboxes.filter((c) => !defaults.includes(c));

      defaults.forEach((field) => {
        setFieldValue(field, true);
      });

      nonDefaults.forEach((field) => {
        setFieldValue(field, false);
      });
    }
  }, [providerType, setFieldValue]);

  // Clear otherDescription when Other checkbox is unchecked
  useEffect(() => {
    if (!other) {
      setFieldValue("otherDescription", "");
      setFieldTouched("otherDescription", false);
    }
  }, [other, setFieldValue, setFieldTouched]);

  return (
    <Grid container direction="column">
      <Typography variant="body2" sx={{ mb: 2 }}>
        Send the client a release of information request for this{" "}
        {values.teamMemberId !== undefined && values.teamMemberId !== null
          ? "team member"
          : "provider"}
      </Typography>

      <Box sx={{ mb: 2 }}>
        <TextField
          name="document.title"
          placeholder="Insert title"
          label="Title of request (internal use only)"
          helperText={(touched.document as any)?.title && (errors.document as any)?.title}
          error={(touched.document as any)?.title && !!(errors.document as any)?.title}
          value={values.document?.title || ""}
          onChange={handleChange}
          onBlur={handleBlur}
          required
          fullWidth
        />
      </Box>

      <Typography
        sx={{
          fontSize: "12px",
          fontWeight: 500,
          lineHeight: "14px",
          letterSpacing: "0.3px",
          color: "text.secondary",
          my: 2,
        }}
      >
        Disclosure Information
      </Typography>

      <Grid item sx={{ mb: 1 }}>
        <FormControlLabel
          control={
            <Checkbox
              name="historyAndPhysicalExamination"
              checked={!!values.historyAndPhysicalExamination}
              onChange={handleChange}
              onBlur={handleBlur}
            />
          }
          label="History & physical examination"
        />
      </Grid>

      <Grid item sx={{ mb: 1 }}>
        <FormControlLabel
          control={
            <Checkbox
              name="diagnosticReportOrTesting"
              checked={!!values.diagnosticReportOrTesting}
              onChange={handleChange}
              onBlur={handleBlur}
            />
          }
          label="Diagnostic report or testing"
        />
      </Grid>

      <Grid item sx={{ mb: 1 }}>
        <FormControlLabel
          control={
            <Checkbox
              name="progressNotes"
              checked={!!values.progressNotes}
              onChange={handleChange}
              onBlur={handleBlur}
            />
          }
          label="Progress notes"
        />
      </Grid>

      <Grid item sx={{ mb: 1 }}>
        <FormControlLabel
          control={
            <Checkbox
              name="psychologicalReports"
              checked={!!values.psychologicalReports}
              onChange={handleChange}
              onBlur={handleBlur}
            />
          }
          label="Psychological reports"
        />
      </Grid>

      <Grid item sx={{ mb: 1 }}>
        <FormControlLabel
          control={
            <Checkbox
              name="educationPlan"
              checked={!!values.educationPlan}
              onChange={handleChange}
              onBlur={handleBlur}
            />
          }
          label="Education plan / IEP / 504 plan"
        />
      </Grid>

      <Grid item sx={{ mb: 1 }}>
        <FormControlLabel
          control={
            <Checkbox
              name="treatmentPlanAndGoals"
              checked={!!values.treatmentPlanAndGoals}
              onChange={handleChange}
              onBlur={handleBlur}
            />
          }
          label="Treatment plan & goals"
        />
      </Grid>

      <Grid item sx={{ mb: 2 }}>
        <FormControlLabel
          control={
            <Checkbox
              name="other"
              checked={!!values.other}
              onChange={handleChange}
              onBlur={handleBlur}
            />
          }
          label="Other"
        />
      </Grid>

      {other && (
        <Box sx={{ mb: 2 }}>
          <TextField
            multiline
            rows={3}
            name="otherDescription"
            placeholder="Description of Requested Information"
            label="Other"
            helperText={touched.otherDescription && errors.otherDescription}
            error={touched.otherDescription && !!errors.otherDescription}
            value={values.otherDescription || ""}
            onChange={handleChange}
            onBlur={handleBlur}
            required={!!other}
            fullWidth
          />
        </Box>
      )}

      {submitAttempted && errors.checkboxes && (
        <Typography color="error" variant="caption">
          {errors.checkboxes}
        </Typography>
      )}
    </Grid>
  );
};

export default ProvidersFormDialogROIForm;
